import React from "react";
import { toast } from "sonner";
import { TrendingUp, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import "../../pages/sections/v19shared.css";
import "../../pages/sections/v20extra.css";

// Rejection Analyzer — faithful v20 mock (Part F4). Patterns across rejected claims, ranked by recoverable $.
const KPIS = [
  { lbl: "Rejected (30d)", val: "2,356", sub: "3.2% of sent" },
  { lbl: "Recoverable", val: "$1.39M", sub: "if reworked", green: 1 },
  { lbl: "Top driver", val: "A7:562", sub: "Member ID", sm: 1 },
  { lbl: "Preventable", val: "71%", sub: "by a scrubber rule" },
];

const PATTERNS = [
  { code: "A7:562", reason: "Member ID / name mismatch", count: 58, risk: "$41,200", conc: "Aetna · front-desk intake", act: "draft" },
  { code: "A6:560", reason: "Missing prior-auth", count: 41, risk: "$38,900", conc: "Aetna · PT codes 97110/97140", act: "draft" },
  { code: "CO-16", reason: "Missing / invalid information", count: 33, risk: "$22,400", conc: "Cigna · spread evenly", act: "view" },
  { code: "A8:454", reason: "Invalid procedure code", count: 19, risk: "$14,100", conc: "truncated ICD-10 parents", act: "draft" },
];

export function RejectionAnalyzer({ open, onOpenChange }) {
  const draft = (p) => { onOpenChange(false); toast.success(`Draft scrubber rule from “${p.code} · ${p.reason}” — ${p.risk} at risk`); };
  const viewClaims = (p) => { onOpenChange(false); toast.message(`Opened ${p.count} claims in a worklist`); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[780px] p-0 gap-0 overflow-hidden" data-testid="rejection-analyzer-modal">
        <DialogTitle className="sr-only">Rejection Analyzer</DialogTitle>
        <DialogDescription className="sr-only">Patterns across your rejected claims, ranked by recoverable dollars.</DialogDescription>
        <div className="v19">
          <div className="ra-hdr">
            <div className="ra-ico"><TrendingUp size={18} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="ra-title">Rejection Analyzer</div>
              <div className="ra-sub">The research cousin of ShAWL denial intelligence — patterns across your rejected claims, ranked by recoverable dollars.</div>
            </div>
            <button className="ra-x" onClick={() => onOpenChange(false)} data-testid="rejection-analyzer-close" aria-label="Close"><X size={16} /></button>
          </div>
          <div className="ra-body">
            <div className="grid4" style={{ marginBottom: 14 }} data-testid="rejection-analyzer-kpis">
              {KPIS.map((k) => (
                <div key={k.lbl} className="kc glass">
                  <div className="kc-lbl">{k.lbl}</div>
                  <div className="kc-val" style={{ ...(k.green ? { color: "var(--green-deep)" } : {}), ...(k.sm ? { fontSize: 15 } : {}) }}>{k.val}</div>
                  <div className="kc-sub">{k.sub}</div>
                </div>
              ))}
            </div>
            <div className="panel glass">
              <div className="panel-hdr"><div className="panel-title">Rejection patterns</div><span style={{ fontSize: 9.5, color: "var(--gray3)" }}>click → draft a scrubber rule</span></div>
              <table className="tbl" data-testid="rejection-analyzer-table">
                <tbody>
                  <tr><th>Code</th><th>Reason</th><th>Count</th><th>$ at risk</th><th>Concentrated in</th><th /></tr>
                  {PATTERNS.map((p) => (
                    <tr key={p.code} data-testid={`rejection-row-${p.code}`}>
                      <td className="mono">{p.code}</td><td>{p.reason}</td><td>{p.count}</td><td className="amt">{p.risk}</td><td>{p.conc}</td>
                      <td>
                        {p.act === "draft"
                          ? <button className="row-act" onClick={() => draft(p)} data-testid={`rejection-draft-${p.code}`}>Draft rule</button>
                          : <button className="row-act ghost" onClick={() => viewClaims(p)} data-testid={`rejection-view-${p.code}`}>View claims</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="v20-devnote" style={{ marginTop: 14 }}><span><b>Dev note</b> — Rejection patterns and recoverable-dollar estimates are mocked sample data — wire to the live rejection/999-277CA feed and the scrubber-rule engine when available.</span></div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
